import { combineStrings } from 'assets/utils/data/String';
import DealerClaimType from 'models/dealerManagement/dealerClaimType/Model.DealerClaimType';
import DealerGroupClaimType from 'models/dealerManagement/dealerGroupClaimType/Model.DealerGroupClaimType';
import Model from 'models/Model';
import Product from '../../productManagement/product/Model.Product';
import ClaimJob from '../claimJob/Model.ClaimJob';
import ClaimTypeAggregate from '../claimTypeAggregate/Model.ClaimTypeAggregate';
import ClaimTypeAutomationRule from '../claimTypeAutomationRule/Model.ClaimTypeAutomationRule';
import ClaimTypeDataPoint from '../claimTypeDataPoint/Model.ClaimTypeDataPoint';
import ClaimTypeDocument from '../claimTypeDocument/Model.ClaimTypeDocument';
import ClaimTypeJobItemTemplate from '../claimTypeJobItemTemplate/Model.ClaimTypeJobItemTemplate';
import ClaimTypeReportSettings from '../claimTypeReportSettings/Model.ClaimTypeReportSettings';
import LossCode from '../lossCode/Model.LossCode';
import claimTypeDeleteApi from './delete/Api.ClaimType.Delete';
import claimTypeListApi from './list/Api.ClaimType.List';
import claimTypeListPublicApi from './listPublic/Api.ClaimType.ListPublic';
import claimTypeSaveApi from './save/Api.ClaimType.Save';

export default class ClaimType extends Model<Model.IClaimType>({ icon: 'fas-cloud' }) implements Model.Instance<Model.IClaimType> {
  id: number;
  version: number;
  code: string;
  name: string;
  description?: string;
  products: Product[];
  legacyClaimTypeId?: number;

  lossCodes: LossCode[];

  claimTypeAggregates: ClaimTypeAggregate[];
  claimTypeReportSettings: ClaimTypeReportSettings[];
  claimTypeJobItemTemplates: ClaimTypeJobItemTemplate[];

  claimTypeAggregatesInactive: ClaimTypeAggregate[];
  claimTypeReportSettingsInactive: ClaimTypeReportSettings[];
  claimTypeJobItemTemplatesInactive: ClaimTypeJobItemTemplate[];

  //#region ClaimTypeDataPoints
  claimTypeDataPoints: ClaimTypeDataPoint[];
  claimTypeDataPointsInactive: ClaimTypeDataPoint[];
  allClaimTypeDataPoints: ClaimTypeDataPoint[];
  //#endregion

  //#region ClaimTypeDocuments
  claimTypeDocuments: ClaimTypeDocument[];
  claimTypeDocumentsInactive: ClaimTypeDocument[];
  allClaimTypeDocuments: ClaimTypeDocument[];
  //#endregion

  isDeleted?: boolean;
  allowManualJobItemsCreation: boolean;
  claimJobs: ClaimJob[];

  //#region Claim Automation
  claimTypeJobItemsAutomationEnabled?: boolean;
  dealerClaimTypes: DealerClaimType[];
  dealerGroupClaimTypes: DealerGroupClaimType[];
  claimTypeAutomationRules: ClaimTypeAutomationRule[];
  //#endregion

  constructor(data: Partial<Model.IClaimType> | Utils.FormData<Model.IClaimType>, language?: Locale.Languages) {
    super(
      {
        products: [Product],
        lossCodes: [LossCode],
        claimTypeAggregates: [ClaimTypeAggregate],
        claimTypeDocuments: [ClaimTypeDocument],
        claimTypeDataPoints: [ClaimTypeDataPoint],
        claimTypeJobItemTemplates: [ClaimTypeJobItemTemplate],
        claimTypeAutomationRules: [ClaimTypeAutomationRule],
        claimTypeReportSettings: [ClaimTypeReportSettings],
        claimTypeAggregatesInactive: [ClaimTypeAggregate],
        claimTypeDocumentsInactive: [ClaimTypeDocument],
        claimTypeReportSettingsInactive: [ClaimTypeReportSettings],
        claimTypeDataPointsInactive: [ClaimTypeDataPoint],
        claimTypeJobItemTemplatesInactive: [ClaimTypeJobItemTemplate],
        dealerClaimTypes: [DealerClaimType],
        dealerGroupClaimTypes: [DealerGroupClaimType],
        allClaimTypeDocuments: [ClaimTypeDocument],
        allClaimTypeDataPoints: [ClaimTypeDataPoint],
      },
      language
    );
    this.update(data);

    //if(this.product?.code === Product.CODE_TRUE_GAP) this.claimTypeReportSettings = [trueGapReportMocup]
    //if(this.product?.code === Product.CODE_FLM) this.claimTypeReportSettings = [flmReportMocup]
  }
  update(data: Partial<Model.IClaimType> | Utils.FormData<Model.IClaimType>) {
    data.allClaimTypeDocuments = data.claimTypeDocuments;
    data.allClaimTypeDataPoints = data.claimTypeDataPoints;

    data.claimTypeAggregatesInactive = data.claimTypeAggregates?.filter((it) => it.version !== data.version);
    data.claimTypeDocumentsInactive = data.claimTypeDocuments?.filter((it) => it.version !== data.version);
    data.claimTypeReportSettingsInactive = data.claimTypeReportSettings?.filter((it) => it.version !== data.version);
    data.claimTypeDataPointsInactive = data.claimTypeDataPoints?.filter((it) => it.version !== data.version);
    data.claimTypeJobItemTemplatesInactive = data.claimTypeJobItemTemplates?.filter(
      (it) => it.version !== data.version
    );

    data.claimTypeAggregates = data.claimTypeAggregates?.filter((it) => it.version == data.version);
    data.claimTypeDocuments = data.claimTypeDocuments?.filter((it) => it.version == data.version);
    data.claimTypeReportSettings = data.claimTypeReportSettings?.filter((it) => it.version == data.version);
    data.claimTypeDataPoints = data.claimTypeDataPoints?.filter((it) => it.version == data.version);
    data.claimTypeJobItemTemplates = data.claimTypeJobItemTemplates?.filter((it) => it.version == data.version);

    super.update(data);
  }
  get displayInfo(): Model.DisplayInfo {
    return {
      id: this.id,
      title: combineStrings(' - ', this.name, this.code) || this.id?.toString(),
      info: this.products?.map((it) => it.name).join(', '),
      subtitle: this.description,
    };
  }
  static async list(body?: Api.Core.ClaimType.List.IRequest) {
    const { payload, ...rest } = await claimTypeListApi(body);
    return {
      ...rest,
      payload: payload && {
        data: payload.data?.map((it) => new ClaimType(it)),
        count: payload.count,
      },
    };
  }
  static async listPublic(req: Api.Core.ClaimType.ListPublic.IRequest) {
    const { payload, ...rest } = await claimTypeListPublicApi(req);
    return {
      ...rest,
      payload: payload && {
        data: payload.data?.map((it) => new ClaimType(it)),
        count: payload.count,
      },
    };
  }
  async save() {
    return await claimTypeSaveApi(this);
  }
  async delete() {
    return await claimTypeDeleteApi(this.id);
  }
}
